/**
 * Contact-Us Center service.
 *
 * Reads the public Board endpoints so the /contact-use page renders real data:
 *  - Sub-tabs come from tab management  -> GET  /api/{group}/tabs   (visibility-filtered server-side)
 *  - Posts in a tab                     -> POST /api/{group}/tabs/{tabId}/posts/paged
 *  - Post detail (+ view count)         -> GET  /api/{group}/posts/{id}
 *  - 1:1 Inquiry                        -> POST /api/ContactUsManagement
 *
 * The board group token matches the controller route segment (CustomerCenter | InfoBoard).
 */
import { axiosInstance } from "../axios/axiosConfig";
import { postContactUs } from "./contactUsService";

export const BOARD_GROUPS = {
  CUSTOMER_CENTER: "CustomerCenter",
  INFO_BOARD: "InfoBoard",
};

/** Tab view-type -> which UI a tab renders with. Mirrors AppConstants.Boards.TabViewType. */
export const TAB_VIEW_TYPE = {
  GENERAL_LIST: "GENERAL_LIST",
  ACCORDION_VIEW: "ACCORDION_VIEW",
  POST_GALLERY: "POST_GALLERY",
};

/* ------------------------------- Board tabs ------------------------------- */
// Already filtered by the caller's login/role (Visibility) on the server.
export const getBoardTabs = async (group) => {
  const res = await axiosInstance.get(`/${group}/tabs`);
  return res.data || [];
};

/* ------------------------------- Board posts ------------------------------ */
// Published, target-matching posts in a tab. Pinned posts float to the top server-side.
export const getBoardPosts = async (
  group,
  tabId,
  { first = 0, rows = 10, search = "", searchKeys, categoryId } = {}
) => {
  const body = {
    first,
    rows,
    multiSortMeta: [
      { field: "isPinned", order: -1 },
      { field: "createdTime", order: -1 },
    ],
  };
  if (search) {
    body.globalFilter = search;
    body.globalFilterKeys = searchKeys || ["titleKo", "titleEn", "titleVi"];
  }
  // FAQ category filter — applied server-side (entity-level) by PublicBoardService.
  if (categoryId) {
    body.filters = { categoryId: { value: String(categoryId), matchMode: "equals" } };
  }
  const res = await axiosInstance.post(
    `/${group}/tabs/${tabId}/posts/paged`,
    body
  );
  return res.data; // { data: [...], pagingInfo: { totalRecordCount } }
};

export const getBoardPostDetail = async (group, id) => {
  const res = await axiosInstance.get(`/${group}/posts/${id}`);
  return res.data;
};

// Detail resolved by the post's public GUID — backs the /contact-us ?postId= detail page.
export const getBoardPostDetailByPublicId = async (group, publicId) => {
  const res = await axiosInstance.get(`/${group}/posts/by-public-id/${publicId}`);
  return res.data;
};

/* ----------------------------- 1:1 문의 Inquiry --------------------------- */
// Inquiry category & writer-type labels are localized in the component from the shared
// CONTACT_US_CATEGORY_OPTIONS / CONTACT_US_TYPE_OPTIONS constants; the chosen codes are
// forwarded to ContactUs as Category / Type.
export const submitInquiry = async (form) => {
  // Map the form to the ContactUs create contract (Name/Email/Subject/Message/Type/Category).
  return postContactUs({
    name: form.name,
    email: form.email,
    subject: form.subject,
    message: form.message,
    type: form.type,
    category: form.category,
  });
};

/* --------------------------- 수강 상담 Consultation ----------------------- */
// Enrollment consultation request (lead). Persists via the public consultation endpoint.
// Phone is sent with its dial code prefixed; the dropdown fields forward their item codes.
export const submitConsultation = async (form) => {
  const phone = [form.phoneCode, form.phone].filter(Boolean).join(" ").trim();
  const res = await axiosInstance.post("/EnrollmentConsultations", {
    name: form.name,
    email: form.email,
    phone: phone || null,
    preferredContactMethod: form.contactMethod || null,
    country: form.country || null,
    preferredTime: form.preferredTime || null,
    interestedCourse: form.interestedCourse || null,
    koreanLevel: form.koreanLevel || null,
    message: form.request,
  });
  return res.data;
};
